import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBoxFull } from '@fortawesome/pro-regular-svg-icons/faBoxFull'

import { InstantSearch, Configure, useHits } from 'react-instantsearch'
import { useProductSearch } from '../../utils/useProductSearch'
import isObjectLike from 'lodash/isObjectLike'
import sortBy from 'lodash/sortBy'
import { formatPriceFromPence } from '../../utils/formatPrice'
import EmblaCarousel from '../../utils/EmblaCarousel'
import { isInStorefront } from '../../utils/isInStorefront'

let Icon = () => (
    <div className="flex items-center justify-center w-full py-10">
        <FontAwesomeIcon icon={faBoxFull} size="6x" />
    </div>
)

const ProductCard = ({
    isEditMode,
    product,
    cardStyle,
    progressIndicatorStyle,
    imageFit,
    showPrices,
    incVAT,
    useSiloImages,
    fontFamily,
    fontWeight,
    fontColour,
    accentColour,
    arrowColour,
    isFirst,
    isLast,
    index,
    fallbackImage,
    ...restProps
}) => {
    const images = product.resources?.filter(r => r.type === 'image')
    const siloImages = product.resources?.filter(r => r.type === 'silo')
    const firstImage = images ? sortBy(images, ['displayOrder'])[0] : null
    const firstSiloImage = siloImages ? sortBy(siloImages, ['displayOrder'])[0] : null

    const [imageLocation, setImageLocation] = useState(firstImage?.location)

    return (
        <a
            style={{ color: fontColour, fontFamily: fontFamily, fontWeight: fontWeight }}
            className="!no-underline hover:!underline"
            href={`/products/${product.slug}`}
            onClick={e => {
                e.preventDefault()
                if (isEditMode) {
                    return null
                } else {
                    window.location = `/products/${product.slug}`
                }
            }}
            onMouseEnter={() => {
                if (useSiloImages && firstSiloImage?.location) {
                    setImageLocation(firstSiloImage.location)
                }
            }}
            onMouseLeave={() => {
                if (useSiloImages && firstSiloImage && (firstImage?.location || fallbackImage)) {
                    setImageLocation(firstImage?.location || fallbackImage || '')
                }
            }}
        >
            <div
                className={`${
                    cardStyle === 'square' ? 'aspect-square' : 'aspect-[3/4]'
                } w-full relative mb-[15px] border border-brand-grey`}
                {...restProps}
            >
                <img
                    src={imageLocation || fallbackImage || undefined}
                    {...(isObjectLike(product.attributes) && {
                        alt: product.attributes.product_name,
                    })}
                    className={`${
                        imageFit === 'cover' ? 'object-cover' : 'object-contain'
                    } absolute top-0 left-0 w-full h-full`}
                    data-testid={`product-card__media`}
                />
            </div>
            <p
                style={{ fontFamily: fontFamily }}
                className="text-[18px] leading-[16px]"
                data-testid={`product-card__name`}
            >
                {isObjectLike(product.attributes) && product.attributes.product_name}
            </p>
            {showPrices && (
                <p
                    style={{ fontFamily: fontFamily }}
                    className="text-[18px] leading-[16px] mt-[5px]"
                    data-testid={`product-card__price`}
                >
                    <span>
                        {formatPriceFromPence(
                            incVAT
                                ? product?.prices?.cheapestPrice?.inc
                                : product?.prices?.cheapestPrice?.exc,
                            '£'
                        )}
                    </span>
                    <span>{incVAT ? ' (inc VAT)' : ' (ex VAT)'} </span>
                </p>
            )}
        </a>
    )
}

const Hits = props => {
    const {
        hits,
        isEditMode,
        cardStyle,
        progressIndicatorStyle,
        imageFit,
        showPrices,
        incVAT,
        useSiloImages,
        fontWeight,
        fontFamily,
        fontColour,
        accentColour,
        arrowColour,
        fallbackImage,
    } = props

    const inStorefront = isInStorefront()

    return hits.length > 0 ? (
        <EmblaCarousel
            accentColour={accentColour}
            arrowColour={arrowColour}
            progressIndicatorStyle={progressIndicatorStyle}
        >
            {hits.map((hit, index) => (
                <div key={hit?.slug || `hit_${index}`} className="flex-[0_0_235px]">
                    <ProductCard
                        isEditMode={isEditMode}
                        product={hit}
                        cardStyle={cardStyle}
                        imageFit={imageFit}
                        showPrices={showPrices}
                        incVAT={incVAT}
                        useSiloImages={useSiloImages}
                        fontFamily={fontFamily}
                        fontWeight={fontWeight}
                        fontColour={fontColour}
                        accentColour={accentColour}
                        arrowColour={arrowColour}
                        fallbackImage={fallbackImage}
                        index={index}
                        isFirst={index === 0}
                        isLast={index === hits.length - 1}
                    />
                </div>
            ))}
        </EmblaCarousel>
    ) : isEditMode || !inStorefront ? (
        <Icon />
    ) : null
}
const CustomHits = props => {
    const { items } = useHits()

    return <Hits {...props} hits={items} />
}

const ProductsCarouselContent = ({
    numberOfProducts = 4,
    attributes = [],
    attributeOperator = 'and',
    cardStyle = 'square',
    progressIndicatorStyle = 'dots',
    imageFit = 'cover',
    showPrices = false,
    incVAT = false,
    fontFamily = '',
    fontWeight = 'normal',
    fontColour = '#000',
    accentColour = '#000',
    arrowColour = '#FFFFFF',
    fallbackImage = '',
    isEditMode,
    useSiloImages = false,
}) => {
    const { hasConfig, searchClient, indexName, configureProps } = useProductSearch({
        attributes,
        attributeOperator,
        numberOfProducts,
    })

    if (!hasConfig) {
        console.error(
            'No Algolia configuration defined. Please refer to the Peracto documentation.'
        )

        return null
    }

    return (
        <InstantSearch searchClient={searchClient} indexName={indexName}>
            <Configure {...configureProps} />

            <CustomHits
                isEditMode={isEditMode}
                cardStyle={cardStyle}
                progressIndicatorStyle={progressIndicatorStyle}
                imageFit={imageFit}
                showPrices={showPrices}
                incVAT={incVAT}
                useSiloImages={useSiloImages}
                fontFamily={fontFamily}
                fontWeight={fontWeight}
                fontColour={fontColour}
                accentColour={accentColour}
                arrowColour={arrowColour}
                fallbackImage={fallbackImage}
            />
        </InstantSearch>
    )
}

const ProductsCarouselRenderer = ({ state, ...props }) => {
    return <ProductsCarouselContent {...state} {...props} />
}

export default ProductsCarouselRenderer
